import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Stack,
  useMediaQuery,
  useTheme,
  LinearProgress,
  Chip,
  Fade,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import {
  Email,
  Speed,
  TrendingUp,
  People,
  CheckCircle,
  Star,
  Schedule,
  BusinessCenter,
} from '@mui/icons-material';

const countUp = (end, duration = 2000) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, [end, duration]);

  return count;
};

const StatCard = ({ icon, value, label, suffix = '', color, delay = 0 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const animatedValue = countUp(parseInt(value));

  return (
    <Fade in timeout={1000 + delay}>
      <Card
        sx={{
          background: isDark ? alpha('#1e293b', 0.6) : alpha('#ffffff', 0.95),
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          border: `1px solid ${alpha(color, isDark ? 0.2 : 0.15)}`,
          boxShadow: `0 8px 32px ${alpha('#000', isDark ? 0.3 : 0.08)}`,
          p: 4,
          height: '100%',
          transition: 'all 0.4s',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 20px 60px ${alpha(color, 0.25)}`,
            '& .stat-icon': {
              transform: 'scale(1.1) rotate(5deg)',
              background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
            },
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.5)} 100%)`,
          },
        }}
      >
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Box
            className="stat-icon"
            sx={{
              width: 70,
              height: 70,
              borderRadius: 3,
              background: alpha(color, isDark ? 0.2 : 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.4s',
              boxShadow: `0 4px 20px ${alpha(color, 0.2)}`,
            }}
          >
            {React.cloneElement(icon, {
              sx: { fontSize: 36, color },
            })}
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontFamily: '"Sora", "Inter", sans-serif',
            }}
          >
            {animatedValue.toLocaleString()}{suffix}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: isDark ? alpha('#f1f5f9', 0.8) : alpha('#0f172a', 0.7),
            }}
          >
            {label}
          </Typography>
        </Stack>
      </Card>
    </Fade>
  );
};

const StatsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  const primaryGradient = isDark
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)'
    : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)';

  const sectionBg = isDark
    ? alpha('#0f172a', 0.95)
    : 'linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%)';

  const textColor = isDark ? '#f1f5f9' : '#0f172a';
  const accentColor = isDark ? '#818cf8' : '#3b82f6';

  const stats = [
    {
      icon: <Email />,
      value: '50000',
      suffix: '+',
      label: 'Emails Sent Daily',
      color: '#3b82f6',
    },
    {
      icon: <TrendingUp />,
      value: '98',
      suffix: '%',
      label: 'Deliverability Rate',
      color: '#10b981',
    },
    {
      icon: <Speed />,
      value: '99',
      suffix: '.9%',
      label: 'Uptime',
      color: '#06b6d4',
    },
    {
      icon: <Star />,
      value: '4',
      suffix: '.9',
      label: 'User Rating',
      color: '#f59e0b',
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: sectionBg,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.1 : 0.05,
          background: `
            radial-gradient(circle at 30% 40%, ${alpha(accentColor, 0.4)} 0%, transparent 50%),
            radial-gradient(circle at 70% 60%, ${alpha('#8b5cf6', 0.4)} 0%, transparent 50%)
          `,
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Stack spacing={2} sx={{ mb: 6, textAlign: 'center' }}>
          <Chip
            label="Platform Statistics"
            sx={{
              background: primaryGradient,
              color: '#fff',
              fontWeight: 700,
              alignSelf: 'center',
              px: 2,
              py: 2.5,
            }}
          />
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            sx={{
              fontFamily: '"Sora", "Inter", sans-serif',
              fontWeight: 900,
              color: textColor,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            Trusted Performance
          </Typography>
        </Stack>

        {/* Stats Grid */}
        <Grid container spacing={4} justifyContent="center">
          {stats.map((stat, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <StatCard {...stat} delay={idx * 100} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default StatsSection;
